import {
	Breakpoints,
	Container,
	ContainerGutter,
	ContainerPadding,
	CTA,
	Spinner,
	styled,
	Text,
	TextAlignment,
	TokenTextAppearance,
	TokenTextColor,
} from '@volkswagen-onehub/components-core';
import React from 'react';
import { BenefitsHelper } from '../../../../../../helpers/benefits-helper';
import { PdfHuchaContent } from '../../../shared/components/pdf-hucha-content';
import { useHuchaPdfGenerator } from '../../../shared/hooks/use-hucha-pdf-generator';
import { HuchaPosventaValues } from '../../../shared/models/hucha-values';
import { useHuchaPosventaLayerContent } from '../../hooks/use-hucha-posventa-layer-content';
import { BonoPosventaDisclaimerContent } from '../disclaimers/bono-disclaimer-content';
import { PopupBonoPosventaCard } from './popup-bono-posventa-card/popup-bono-posventa-card';
import { normalizeToTimestamp } from '../../../../../../helpers/common-helpers';
import { useCitaHandler } from '../../../shared/hooks/use-cita-handler';

export const StatusIndicator = styled.div<{ status: string }>`
	background: ${(props: { status: string }) => props.status};
	border-radius: 50%;
	width: 16px;
	height: 16px;
`;

export const BonoPosventaPopup = ({ huchaValues }: { huchaValues: HuchaPosventaValues | null }) => {
	if (!huchaValues) return null;

	const targetRefPDF = React.useRef<HTMLDivElement>(null);
	const { handlePdfDownloadClick, isDownloading, isPdfReady } = useHuchaPdfGenerator(huchaValues, targetRefPDF);
	const { handleCita } = useCitaHandler(huchaValues.huchaType);
	const { huchaPosventaPdfLayerContent } = useHuchaPosventaLayerContent(huchaValues);

	const formatDate = (dateTimestamp: number | string | Date | null | undefined): string => {
		if (!dateTimestamp) return '';

		const timestamp = normalizeToTimestamp(dateTimestamp);
		const date = new Date(timestamp);

		return date.getDate() + '/' + (date.getMonth() + 1) + '/' + date.getFullYear();
	};

	return (
		<>
			<Container
				padding={{
					right: {
						[Breakpoints.default]: ContainerPadding.grid001,
						[Breakpoints.b560]: ContainerPadding.grid002,
						[Breakpoints.b960]: ContainerPadding.grid001,
					},
					bottom: ContainerPadding.dynamic0200,
					left: {
						[Breakpoints.default]: ContainerPadding.grid001,
						[Breakpoints.b560]: ContainerPadding.grid002,
						[Breakpoints.b960]: ContainerPadding.grid001,
					},
				}}
				horizontalAlign={'center'}
				wrap={'always'}
			>
				{/* PopupHuchaPosventaCard */}
				<Container verticalAlign={'center'} horizontalAlign={'center'}>
					<PopupBonoPosventaCard dineroHucha={huchaValues.dineroHucha} />
				</Container>

				<Container
					wrap={'always'}
					padding={{
						top: ContainerPadding.dynamic0130,
					}}
					gutter={ContainerGutter.static250}
				>
					<Container
						wrap={'never'}
						gutter={ContainerGutter.static100}
						horizontalAlign={'center'}
						verticalAlign={'center'}
						shrinkContent
					>
						<Container
							padding={{
								right: ContainerPadding.static100,
							}}
						>
							<StatusIndicator status={BenefitsHelper.getHuchaBonusStatusColor(huchaValues.expirationDate)} />
						</Container>

						<Text appearance={TokenTextAppearance.copy200} color={TokenTextColor.primary}>
							{BenefitsHelper.getHuchaBonusValidityMessage(huchaValues.expirationDate)}
						</Text>
					</Container>
					<Text appearance={TokenTextAppearance.headline300} textAlign={TextAlignment.center}>
						<b>Te damos la bienvenida</b> al Club Volkswagen
					</Text>

					<Container
						wrap={'always'}
						gutter={ContainerGutter.static400}
						verticalAlign={'center'}
						horizontalAlign={'center'}
					>
						<Text appearance={TokenTextAppearance.copy200} textAlign={TextAlignment.center}>
							Nos encanta verte y cuidar de tu coche. Por eso, por confiar en nuestro Servicio Oficial y haberte unido
							al club, queremos regalarte: <b>{huchaValues.dineroHucha}€ en tu próximo paso por taller.</b>
						</Text>

						<Text appearance={TokenTextAppearance.copy200} textAlign={TextAlignment.center}>
							Este bono es solo válido para recambios, en operaciones con un importe mínimo de 100€ sin incluir la mano
							de obra.
						</Text>
					</Container>

					<Container verticalAlign={'center'} horizontalAlign={'center'}>
						<CTA
							tag="button"
							aria-label="Descarga tu bono taller"
							size="large"
							onClick={handlePdfDownloadClick}
							emphasis="primary"
							disabled={isDownloading}
							stretchContent={{ [Breakpoints.default]: true, [Breakpoints.b560]: false }}
							icon={isDownloading ? <Spinner /> : undefined}
						>
							<Text>{isDownloading ? 'Descargando' : 'Descarga tu bono taller'}</Text>
						</CTA>
					</Container>

					<Container wrap={'always'} verticalAlign={'center'} horizontalAlign={'center'}>
						<Text appearance={TokenTextAppearance.copy200} textAlign={TextAlignment.center} bold>
							¿Cómo puedes utilizarlo?
						</Text>
						<Text appearance={TokenTextAppearance.copy200} textAlign={TextAlignment.center}>
							En tu próxima visita a taller, muéstraselo a tu asesor y él se encargará del resto.
						</Text>
					</Container>

					<Container padding={{ top: ContainerPadding.static400 }} verticalAlign={'center'} horizontalAlign={'center'}>
						<Text appearance={TokenTextAppearance.copy200} textAlign={TextAlignment.center}>
							¡Recuerda que tienes hasta el {formatDate(huchaValues.expirationDate)} para disfrutar de esta ventaja!
						</Text>
					</Container>

					<Container verticalAlign={'center'} horizontalAlign={'center'}>
						<CTA
							tag="button"
							aria-label="Pide cita en tu Servicio Oficial"
							size="large"
							onClick={handleCita}
							emphasis="primary"
							stretchContent={{ [Breakpoints.default]: true, [Breakpoints.b560]: false }}
						>
							<Text>Pide cita en tu Servicio Oficial</Text>
						</CTA>
					</Container>

					<Container
						wrap={'always'}
						padding={{
							top: {
								[Breakpoints.default]: ContainerPadding.dynamic0200,
								[Breakpoints.b560]: ContainerPadding.dynamic0150,
							},
						}}
						verticalAlign={'center'}
						horizontalAlign={'center'}
					>
						{/* Disclaimers */}
						<Container verticalAlign={'center'} horizontalAlign={'center'}>
							<BonoPosventaDisclaimerContent />
						</Container>
					</Container>
				</Container>
			</Container>
			{isPdfReady && <PdfHuchaContent targetRef={targetRefPDF} layerContent={() => huchaPosventaPdfLayerContent} />}
		</>
	);
};
